import {
    Box,
    Button, HStack, Spinner, Table, TableContainer, Text, Th, Thead, Tr, VStack
} from "@chakra-ui/react";
import {scrollbarCss} from "../../../../theme/theme";
import {AddIcon} from "@chakra-ui/icons";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {
    getCategoriesAllNew,
    getProducersMinify,
    getProductsNew,
    moveProductAfter,
    moveProductBefore
} from "../../../../data/api/Customer";
import {ProductModelNew} from "../../../../data/model/Product";
import {useAppDispatch, useAppSelector} from "../../../../hooks/use-store";
import {
    productCancelFilters,
    setProductCategoryId,
    setProductPage,
    setProductProducerId,
    setProductQuery
} from "../../../../store/reducers";
import {CategoriesResponseNew} from "../../../../data/model/Category";
import {SectionHeader} from "../../../../components/molecules/SectionHeader/SectionHeader";
import {FilterSidebar} from "../../../../components/molecules/FilterSidebar/FilterSidebar";
import {EmptyPage} from "../../../../components/organisms/EmptyPage/EmptyPage";
import {ProductCard, ProductCardLoading} from "../../../../components/molecules/ProductCard/ProductCard";
import {FaArchive} from "react-icons/all";
import {ColorsClass} from "../../../../theme/colors";
import {useInfiniteQuery} from "@tanstack/react-query";
import {getData} from "../../../../utils/Utils";
import {SelectorElement} from "../../../../components/molecules/OneElementSelector/OneElementSelector";
import {DragDropContext, Draggable, Droppable, DropResult} from "@hello-pangea/dnd";

interface ProductsResponse {
    isSuccess: boolean;
    statusCode: { value: number; description: string };
    data: {
        isLastPage: boolean;
        totPage: number;
        currentPage: number;
        data: ProductModelNew[];
    };
}

export default function ProductsPage() {
    const userType = localStorage.getItem('userType') || 'seller';
    const {query, producerId, categoryId} = useAppSelector(s => s.productPage);
    const [archived, setArchived] = useState(false);
    const [cards, setCards] = useState<ProductModelNew[]>([]);
    const [resetKey, setResetKey] = useState(0);
    const [showLoader, setShowLoader] = useState(true);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetchingNextPage,
        isRefetching,
        refetch
    } = useInfiniteQuery({
        queryKey: ['get-products-infinite', categoryId, producerId, query, archived, resetKey],
        queryFn: async ({pageParam = 0}) => {
            const res = await getProductsNew({
                userType,
                page: pageParam,
                categoryId,
                producerId,
                search: query,
                archived
            });
            return res?.data as ProductsResponse;
        },
        getNextPageParam: (lastPage, allPages) => {
            return !lastPage?.data?.isLastPage
                ? allPages.length
                : undefined;
        },
        initialPageParam: 0
    });

    useEffect(() => {
        if (!data) return
        setCards(data?.pages?.flatMap(p => p?.data?.data || []))
    }, [data, archived])

    const reorder = useCallback(<T, >(list: T[], from: number, to: number) => {
        const clone = Array.from(list)
        const [moved] = clone.splice(from, 1)
        clone.splice(to, 0, moved)
        return clone
    }, [])

    const onDragEnd = async (result: DropResult) => {
        const {source, destination} = result
        if (!destination) return
        if (source.index === destination.index) return

        const newOrder = reorder(cards, source.index, destination.index)
        setCards(newOrder)

        const dragged = cards[source.index]
        const target = cards[destination.index]
        if (destination.index < source.index) {
            await moveProductAfter({productId: dragged.id || "", targetId: target.id || ""})
        } else {
            await moveProductBefore({productId: dragged.id || "", targetId: target.id || ""})
        }

        setResetKey(prevState => prevState + 1);
        setShowLoader(false);
        refetch().finally(() => {
            setShowLoader(true);
        })
    }

    return (
        <HStack w="100%" h="100%">
            <VStack w="70%" h="100%" pt="24px" alignItems="flex-start" overflow="hidden">
                <SectionHeader
                    title={userType === 'admin' ? 'Prodotti' : 'I miei prodotti'}
                    rightSection={
                        <HStack mr={6}>
                            <Button
                                w={164}
                                onClick={() => setArchived(v => !v)}
                                backgroundColor={archived ? ColorsClass.chatbotBlue : undefined}
                                _hover={{
                                    backgroundColor: archived
                                        ? ColorsClass.chatbotBlueHover
                                        : ColorsClass.white
                                }}
                                gap={2}
                            >
                                <FaArchive
                                    size={14}
                                    color={archived ? ColorsClass.white : ColorsClass.chatbotBlue}
                                />
                                <Text color={archived ? ColorsClass.white : ColorsClass.chatbotBlue}>
                                    {archived ? 'Tutti' : 'Archiviati'}
                                </Text>
                            </Button>
                        </HStack>
                    }
                />

                {!isLoading && cards.length === 0 ? (
                    <EmptyPage
                        stackProps={{width: '100%'}}
                        title="Nessun prodotto trovato"
                        description="Prova a cambiare i filtri o ad aggiungere nuovi prodotti."
                    />
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="products-droppable">
                            {provided => (
                                <VStack
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    mx="24px"
                                    mt="24px"
                                    pb="48px"
                                    gap="16px"
                                    alignSelf="stretch"
                                    overflowY="scroll"
                                    borderRadius="16px"
                                    css={scrollbarCss({marginLeft: 0, marginRight: 0})}
                                >
                                    {((isLoading || isRefetching) && showLoader) &&
                                        Array.from({length: 5}).map((_, i) => (
                                            <ProductCardLoading key={i}/>
                                        ))}

                                    {!((isLoading || isRefetching) && showLoader) && cards.map((product, idx) => (
                                        <Draggable
                                            key={product.id}
                                            draggableId={product.id || ""}
                                            index={idx}
                                        >
                                            {(prov, snap) => (
                                                <Box
                                                    ref={prov.innerRef}
                                                    {...prov.draggableProps}
                                                    {...prov.dragHandleProps}
                                                    w="95%"
                                                    mb="16px"
                                                    borderRadius="12px"
                                                >
                                                    <DraggableProductCard
                                                        product={product}
                                                        index={idx}
                                                        doRefetch={() => {
                                                            setResetKey(prevState => prevState + 1);
                                                            refetch();
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}

                                    {provided.placeholder}

                                    {hasNextPage && (
                                        <Button
                                            onClick={() => fetchNextPage()}
                                            isDisabled={isLoading}
                                            mt="8px"
                                            px={16}
                                            py={4}
                                        >
                                            {isFetchingNextPage ? <Spinner/> : "Carica altri"}
                                        </Button>
                                    )}
                                </VStack>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}

                <HStack
                    cursor="pointer"
                    h="64px"
                    px="24px"
                    borderRadius="24px"
                    position="absolute"
                    alignItems="center"
                    justifyContent="center"
                    bottom="16px"
                    right="16px"
                    backgroundColor="secondary.200"
                    _hover={{backgroundColor: 'tertiary.200'}}
                    onClick={() => (window.location.href = '/products/create')}
                >
                    <AddIcon w="16px" h="16px"/>
                    <Text ms="8px" fontWeight="bold">
                        Aggiungi
                    </Text>
                </HStack>
            </VStack>

            <VStack h="100%" w="30%" backgroundColor="surfaceContainer.light">
                <ProductFilterSelector/>
            </VStack>
        </HStack>
    )
}

// interface DraggableProductCardProps {
//     product: ProductModelNew;
//     index: number;
//     onDrop: (
//         draggedId: string,
//         targetId: string,
//         before: boolean
//     ) => Promise<void> | void;
// }

interface DraggableProductCardProps {
    product: ProductModelNew;
    index: number;
    doRefetch: () => void;
}

export const DraggableProductCard: React.FC<DraggableProductCardProps> = ({
                                                                              product,
                                                                              index,
                                                                              doRefetch,
                                                                              // onDrop,
                                                                              // moveCard,
                                                                          }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    return (
        <div
            ref={ref}
            style={{
                cursor: 'grab',
                width: '95%'
            }}
        >
            <ProductCard
                data={product}
                doRefetch={() => {
                    doRefetch();
                }}
                isLoading={false}
            />
        </div>
    );
};

const ProductFilterSelector = () => {
    const [openCategoryField, setOpenCategoryField] = useState(false);
    const [openProducerField, setOpenProducerField] = useState(false);
    const [selectCategory, setSelectCategory] = useState<SelectorElement>();
    const [selectProducer, setSelectProducer] = useState<SelectorElement>();

    const [searchInput, setSearchInput] = useState<string | undefined>();

    const dispatch = useAppDispatch();

    const responseAll = useQuery({
        queryKey: ['get-categories-all-all'],
        queryFn: () => getCategoriesAllNew(),
        refetchOnWindowFocus: "always"
    });

    const {data: producersData} = useQuery({queryKey: ["producer-mine-minify"], queryFn: () => getProducersMinify()})
    const producers = getData<ProductModelNew[]>(producersData) || [];

    const {data: dataCategoriesAll} = responseAll || {};
    const categoriesAllDataResponse = useMemo(() => dataCategoriesAll?.data as CategoriesResponseNew || {}, [dataCategoriesAll?.data]);
    const categoriesAllData = useMemo(() => categoriesAllDataResponse?.data || [], [categoriesAllDataResponse]);

    // const [itemsCategoryList, setCategoriesAll] = useState<SelectorElement[]>([]);
    // const [itemsProducerList, setProducersAll] = useState<SelectorElement[]>([]);

    const categoriesList = useMemo(() => {
        return (categoriesAllData || []).map(value => ({
            id: value.id || "",
            name: value.name || ""
        }));
    }, [categoriesAllData]);

    const producersList = useMemo(() => {
        return (producers || []).map(value => ({
            id: value.id || "",
            name: value.name || ""
        }));
    }, [producers]);

    const isAdmin = localStorage.getItem("userType") === 'admin';

    const selectors = useMemo(() => {
        const list = [{
            fieldName: "Filtra per categoria",
            placeholder: "Seleziona una categoria...",
            openField: openCategoryField,
            setOpenField: setOpenCategoryField,
            items: categoriesList,
            selectedItem: selectCategory,
            setSelectedItem: setSelectCategory
        }];

        if (isAdmin) {
            list.push(
                {
                    fieldName: "Filtra per produttore",
                    placeholder: "Seleziona un produttore...",
                    openField: openProducerField,
                    setOpenField: setOpenProducerField,
                    items: producersList,
                    selectedItem: selectProducer,
                    setSelectedItem: setSelectProducer
                });
        }

        return list;
    }, [categoriesList, isAdmin, openCategoryField, openProducerField, producersList, selectCategory, selectProducer]);

    return <FilterSidebar
        searchProps={{
            searchInput,
            onChangeText: setSearchInput,
            placeholder: "Cerca un prodotto..."
        }}
        selectors={selectors}
        buttons={{
            cancel: {
                text: "Annulla",
                onClick: () => {
                    setSelectCategory(undefined);
                    setSelectProducer(undefined);
                    setSearchInput("");

                    dispatch(productCancelFilters());
                }
            },
            filter: {
                text: "Filtra",
                onClick: () => {
                    dispatch(setProductPage(1));
                    selectCategory && dispatch(setProductCategoryId(selectCategory?.id || ""));
                    selectProducer && dispatch(setProductProducerId(selectProducer?.id || ""));
                    searchInput && dispatch(setProductQuery(searchInput));
                }
            }
        }}
    />
}

interface TableComponentProps {
    headers: string[],
    body: JSX.Element;
}

export const TableComponent = ({headers, body}: TableComponentProps) => {
    return <TableContainer alignSelf={"center"} w={"100%"} mt={"24px"} pb={"16px"}
                           css={scrollbarCss({marginLeft: 0, marginRight: 0})}>
        <Table variant='simple'>
            <Thead>
                <Tr>
                    {headers.map((value, index) => (
                        <Th key={`header_id_${index}`} textColor={"primary.400"}>{value}</Th>
                    ))}
                </Tr>
            </Thead>
            {body}
        </Table>
    </TableContainer>
}

export type DropHandler =
    (draggedId: string, targetId: string, before: boolean) => Promise<void>;
